"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "@/hooks/use-translations";
import { QrCode as QrCodeIcon } from "lucide-react";

const IOS_APP_STORE_URL =
  process.env.NEXT_PUBLIC_IOS_APP_STORE_URL ||
  "https://apps.apple.com/app/growthsync/id6756941263";
const ANDROID_PLAY_STORE_URL =
  process.env.NEXT_PUBLIC_ANDROID_PLAY_STORE_URL ||
  "https://play.google.com/store/apps/details?id=com.growthsync.network";

/**
 * Generates a QR code (as a data URL) pointing at /get-app, which device-detects
 * the scanning phone and redirects iPhones to the App Store and Android phones to
 * Google Play — so one code works for both. Uses the live origin so it resolves
 * correctly on prod and preview deployments.
 */
function useGetAppQr(size = 320) {
  const [qr, setQr] = useState<string | null>(null);
  // Phones never show the QR (see QrCard), so skip the dynamic import and the encode there.
  const phone = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

  useEffect(() => {
    if (phone) return;
    let cancelled = false;

    // The qrcode library is imported here rather than at module scope on purpose. This component is
    // rendered by the landing page, so a top-level import put the whole library (844KB installed)
    // on the critical path of every first visit, to draw one code that most visitors never look at:
    // anyone already on a phone taps a store badge instead. Loading it on demand takes it out of the
    // initial bundle entirely. The QR still appears without any interaction, just a beat later, and
    // `qr` already starts null so there is no new empty state to design.
    (async () => {
      try {
        const { default: QRCode } = await import("qrcode");
        if (cancelled) return;
        const url = `${window.location.origin}/get-app`;
        const dataUrl = await QRCode.toDataURL(url, {
          width: size,
          margin: 1,
          errorCorrectionLevel: "M",
          color: { dark: "#0a0a0a", light: "#ffffff" },
        });
        if (!cancelled) setQr(dataUrl);
      } catch {
        if (!cancelled) setQr(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [size]);

  return qr;
}

/**
 * Official App Store / Google Play badges — for visitors already on a phone.
 * Uses Apple's and Google's own badge artwork (required by their brand
 * guidelines). Google's badge ships with built-in clear space, so it's rendered
 * slightly taller to optically match Apple's pill height.
 */
function StoreButtons() {
  return (
    <div className="flex flex-row flex-wrap items-center justify-center gap-2 md:justify-start">
      <a
        href={IOS_APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Download GrowthSync on the App Store"
        className="inline-flex transition-transform hover:scale-[1.03]"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/badges/app-store-badge.svg"
          alt="Download on the App Store"
          className="h-[46px] w-auto"
        />
      </a>
      <a
        href={ANDROID_PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get GrowthSync on Google Play"
        className="inline-flex transition-transform hover:scale-[1.03]"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/badges/google-play-badge.png"
          alt="Get it on Google Play"
          className="h-[68px] w-auto"
        />
      </a>
    </div>
  );
}

/** Square QR card with a "scan with your phone" caption. */
function QrCard({ size = 208 }: { size?: number }) {
  const { t } = useTranslations("getApp");
  const qr = useGetAppQr(Math.max(size * 1.5, 256));

  return (
    <div className="flex flex-col items-center">
      <div className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.18)]">
        {qr ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={qr}
            alt={t("qrAlt")}
            width={size}
            height={size}
            style={{ width: size, height: size }}
          />
        ) : (
          <div
            className="flex items-center justify-center text-neutral-300"
            style={{ width: size, height: size }}
          >
            <QrCodeIcon className="h-16 w-16" />
          </div>
        )}
      </div>
      <p className="mt-4 flex items-center gap-2 text-sm font-medium text-neutral-500">
        <QrCodeIcon className="h-4 w-4" />
        {t("qrCaption")}
      </p>
    </div>
  );
}

/**
 * Full marketing download section for the landing page.
 */
export function DownloadAppCTA() {
  const { t } = useTranslations("getApp");

  return (
    <section className="px-4 py-14 md:px-6 md:py-20 lg:px-8">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-[2rem] border border-neutral-200 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="grid grid-cols-1 items-center gap-8 p-8 md:grid-cols-2 md:gap-12 md:p-12">
          <div className="text-center md:text-left">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
              {t("eyebrow")}
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-neutral-900 md:text-4xl">
              {t("heading")}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-600 md:text-lg">
              {/* You cannot scan a code with the phone that is displaying it, so the
                  instruction changes with the device rather than the layout alone. */}
              <span className="md:hidden">{t("bodyPhone")}</span>
              <span className="hidden md:inline">{t("bodyDesktop")}</span>
            </p>
            <div className="mt-8">
              <StoreButtons />
            </div>
          </div>
          <div className="hidden md:block">
            <QrCard size={208} />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Compact download block for use inside the logged-in app (e.g. Settings).
 * Renders bare content — wrap it in the host surface's own card/container.
 */
export function GetAppCard() {
  return (
    <div className="grid grid-cols-1 items-center gap-6 sm:grid-cols-2 sm:gap-8">
      <div className="text-center sm:text-left">
        <p className="text-base leading-relaxed text-neutral-600">
          Install GrowthSync on your phone for the best experience. Scan the
          code. It opens the right store for your device automatically.
        </p>
        <div className="mt-6">
          <StoreButtons />
        </div>
      </div>
      <QrCard size={168} />
    </div>
  );
}
